import React, { useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Download, Video, User, Building2, Calendar, ExternalLink, BookOpen, Send } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import { toast } from 'sonner';

const BACKEND_URL = '';

export default function KeynotesPage({ user }) {
  const [keynotes, setKeynotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const submitAccessRequest = async () => {
    setRequestSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/keynotes/request-access`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: requestReason.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Request failed');
      }
      setRequestSent(true);
      toast.success('Request sent — an admin will be in touch shortly.');
    } catch (e) {
      toast.error(e.message || 'Could not send request');
    } finally {
      setRequestSending(false);
    }
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/keynotes`, { credentials: 'include' })
      .then(async r => {
        if (r.status === 403) {
          // Server distinguishes "not an attendee" from a generic auth fail.
          setAccessDenied(true);
          return [];
        }
        if (!r.ok) throw new Error('Failed');
        return r.json();
      })
      .then(data => {
        if (data) setKeynotes(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleDownload = async (id, filename) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/keynotes/${id}/download`, { credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started');
    } catch {
      toast.error('Download failed');
    }
  };

  // Group keynotes by event
  const grouped = keynotes.reduce((acc, k) => {
    const event = k.event_name || 'Other Presentations';
    if (!acc[event]) acc[event] = [];
    acc[event].push(k);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="keynotes" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-8">
            <h1 className="text-4xl font-bold font-heading mb-2">
              SLL <span className="gradient-text">Keynotes</span>
            </h1>
            <p className="text-slate-600">Presentations and videos from SLL events and speakers</p>
            <p className="text-sm text-slate-500 mt-2 italic">Keynote presentations will be uploaded following the event.</p>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto" />
            </div>
          ) : accessDenied ? (
            <div className="text-center py-16 max-w-xl mx-auto" data-testid="keynotes-access-denied">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-sage/15 mb-5">
                <Calendar className="h-8 w-8 text-brand-sage" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">Keynote access is reserved for event attendees</h2>
              <p className="text-slate-600 leading-relaxed">
                Recordings and slide decks from SLL events are made available
                here for everyone who attended. Once you've joined us at an
                event &mdash; in-person or live online &mdash; this page will
                unlock automatically after the event ends.
              </p>
              <p className="text-slate-500 text-sm mt-5">
                Already attended an SLL event &mdash; or need access to a
                historic deck like Manchester SLL? Request access below and
                an admin will follow up.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => { setRequestReason(''); setRequestSent(false); setShowRequest(true); }}
                  data-testid="keynotes-request-access"
                  className="bg-brand-navy hover:bg-brand-dark text-white"
                >
                  <Send className="h-4 w-4 mr-2" /> Request access
                </Button>
                <a
                  href="/events"
                  className="inline-block px-5 py-2.5 rounded-md border border-brand-sage text-brand-sage hover:bg-brand-sage/10 font-medium text-sm"
                  data-testid="keynotes-browse-events"
                >
                  Browse upcoming events
                </a>
              </div>
            </div>
          ) : keynotes.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500">No keynotes available yet.</p>
            </div>
          ) : (
            <div className="space-y-10">
              {Object.entries(grouped).map(([eventName, items]) => (
                <div key={eventName}>
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="h-5 w-5 text-brand-sage" />
                    <h2 className="text-xl font-bold text-slate-800">{eventName}</h2>
                    {items[0]?.event_date && (
                      <span className="text-sm text-slate-500">
                        {new Date(items[0].event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map(k => {
                      const id = k.keynote_id || k.file_id;
                      const isVideo = k.type === 'video';
                      return (
                        <Card key={id} data-testid={`keynote-card-${id}`}
                          className="bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all overflow-hidden">
                          {/* Colored top bar */}
                          <div className={`h-1.5 ${isVideo ? 'bg-red-500' : 'bg-brand-navy'}`} />
                          <div className="p-5">
                            <div className="flex items-start gap-3 mb-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isVideo ? 'bg-red-50' : 'bg-brand-navy/10'}`}>
                                {isVideo ? <Video className="h-5 w-5 text-red-500" /> : <FileText className="h-5 w-5 text-brand-navy" />}
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-base font-bold text-slate-800 leading-tight">{k.title}</h3>
                                {k.subject && (
                                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-brand-sage/15 text-brand-sage text-xs font-medium">
                                    <BookOpen className="h-3 w-3" /> {k.subject}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Presenter & Company */}
                            <div className="space-y-1.5 mb-3">
                              {k.presenter && (
                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                  <User className="h-3.5 w-3.5 text-slate-400" />
                                  <span>{k.presenter}</span>
                                  {k.company && <span className="text-slate-500">at {k.company}</span>}
                                </div>
                              )}
                              {!k.presenter && k.company && (
                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                                  <span>{k.company}</span>
                                </div>
                              )}
                              {k.event_date && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(k.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </div>

                            {k.description && (
                              <p className="text-sm text-slate-600 mb-4 line-clamp-2">{k.description}</p>
                            )}

                            {/* Action button */}
                            {isVideo ? (
                              <a href={k.video_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-full gap-2 px-4 py-2 rounded-md bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors"
                                data-testid={`watch-keynote-${id}`}>
                                <Video className="h-4 w-4" /> Watch Video <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <Button
                                data-testid={`download-keynote-${id}`}
                                onClick={() => handleDownload(id, k.filename)}
                                className="w-full bg-brand-navy hover:bg-brand-dark text-white">
                                <Download className="h-4 w-4 mr-2" /> Download
                                {k.size > 0 && <span className="ml-1 text-xs opacity-70">({(k.size / 1024 / 1024).toFixed(1)} MB)</span>}
                              </Button>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
            </div>
            <PageSideRail placement="keynotes" />
          </div>
        </div>
      </main>

      {/* Request access dialog */}
      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent className="max-w-md" data-testid="keynote-request-dialog">
          <DialogHeader>
            <DialogTitle>Request keynote access</DialogTitle>
            <DialogDescription>
              An admin will be notified and can grant you access to all keynotes
              you've attended or to specific historic decks. Please don't use the
              Feedback section for access requests.
            </DialogDescription>
          </DialogHeader>
          {requestSent ? (
            <div className="py-6 text-center space-y-3" data-testid="keynote-request-sent">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100">
                <Send className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-700 font-medium">Request sent</p>
              <p className="text-xs text-slate-500">
                An admin will be in touch shortly. You can close this dialog.
              </p>
              <Button onClick={() => setShowRequest(false)} variant="outline" data-testid="keynote-request-close">
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Which keynote, or what's the context? (optional)
                </label>
                <Textarea
                  data-testid="keynote-request-reason"
                  value={requestReason}
                  onChange={e => setRequestReason(e.target.value)}
                  placeholder="e.g. I attended the Manchester SLL session in Oct 2025 — could you grant me access?"
                  rows={4}
                  maxLength={500}
                  className="mt-1.5"
                />
                <p className="text-[10px] text-slate-400 mt-1">{requestReason.length}/500</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRequest(false)} disabled={requestSending}>
                  Cancel
                </Button>
                <Button
                  onClick={submitAccessRequest}
                  disabled={requestSending}
                  data-testid="keynote-request-submit"
                  className="bg-brand-navy hover:bg-brand-dark text-white"
                >
                  {requestSending ? 'Sending…' : (<><Send className="h-4 w-4 mr-2" /> Send request</>)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
